import React from 'react';
import { pl } from './locales/pl';
import { en } from './locales/en';

type Language = 'pl' | 'en';
type Dictionary = typeof pl;
type TranslationKey = keyof Dictionary;

const STORAGE_KEY = 'dice1k-language';

interface I18nContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
  languages: { id: Language; label: string }[];
}

const dictionaries: Record<Language, Dictionary> = {
  pl,
  en,
};

const detectLanguage = (): Language => {
  if (typeof window === 'undefined') return 'pl';
  const stored = window.localStorage?.getItem(STORAGE_KEY);
  if (stored === 'pl' || stored === 'en') return stored;
  // Default to Polish unless user explicitly chose otherwise.
  return 'pl';
};

const I18nContext = React.createContext<I18nContextValue | undefined>(undefined);

export function I18nProvider({ children }: { children: React.ReactNode }) {
  const [language, setLang] = React.useState<Language>(detectLanguage());

  const setLanguage = React.useCallback((lang: Language) => {
    setLang((prev) => {
      if (prev === lang) return prev;
      try {
        window.localStorage?.setItem(STORAGE_KEY, lang);
      } catch {}
      return lang;
    });
  }, []);

  const t = React.useCallback<I18nContextValue['t']>((key, params) => {
    const dict = dictionaries[language] || pl;
    const template = dict[key] ?? key;
    if (!params) return template;
    return Object.keys(params).reduce((acc, paramKey) => {
      return acc.replace(`{${paramKey}}`, String(params[paramKey]));
    }, template);
  }, [language]);

  const value = React.useMemo<I18nContextValue>(() => ({
    language,
    setLanguage,
    t,
    languages: [
      { id: 'pl', label: dictionaries.pl['lang.name'] },
      { id: 'en', label: dictionaries.en['lang.name'] },
    ],
  }), [language, setLanguage, t]);

  return (
    <I18nContext.Provider value={value}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n(): I18nContextValue {
  const ctx = React.useContext(I18nContext);
  if (!ctx) {
    throw new Error('useI18n must be used within an I18nProvider');
  }
  return ctx;
}

export type { Language, TranslationKey };
